/**
 * Colunas de técnico empilhadas: 1 slot visual = host + 1..N abaixo.
 * stack map: hostTechnicianName -> string | string[]
 */

export function normalizeStackMembers(value) {
  if (Array.isArray(value)) {
    return value.map((name) => String(name || '').trim()).filter(Boolean);
  }
  const one = String(value || '').trim();
  return one ? [one] : [];
}

/** Técnicos que só aparecem abaixo do host (não renderizam coluna própria). */
export function getStackedBottomTechnicianSet(stacks) {
  const set = new Set();
  if (!stacks || typeof stacks !== 'object') return set;
  Object.values(stacks).forEach((value) => {
    normalizeStackMembers(value).forEach((name) => set.add(name));
  });
  return set;
}

/** Slots da esquerda → direita: hosts (topos), sem bottoms empilhados. */
export function getTechnicianColumnSlots(visibleTechnicians, stacks) {
  const bottoms = getStackedBottomTechnicianSet(stacks);
  return (visibleTechnicians || []).filter((t) => !bottoms.has(t));
}

/**
 * Empilha dragTech sob hostTech (host fica em cima).
 * Libera empilhamentos conflitantes anteriores.
 */
export function applyTechnicianColumnStack(stacks, hostTech, dragTech) {
  const host = String(hostTech || '').trim();
  const drag = String(dragTech || '').trim();
  if (!host || !drag || host === drag) {
    return stacks && typeof stacks === 'object' ? { ...stacks } : {};
  }

  const next = {};
  if (stacks && typeof stacks === 'object') {
    Object.entries(stacks).forEach(([h, b]) => {
      const hostKey = String(h || '').trim();
      const members = normalizeStackMembers(b);
      if (!hostKey || !members.length) return;
      if (hostKey === host || hostKey === drag) return;
      if (members.includes(host) || members.includes(drag)) return;
      next[hostKey] = members.length === 1 ? members[0] : members;
    });
  }
  next[host] = drag;
  return next;
}

/** Empilha grupos consecutivos (2 = A+B; 3 = A+B+C). Sobra à direita fica solta. */
export function applyVerticalGroupStacks(visibleTechnicians, groupSize = 2) {
  const size = Math.max(2, Math.min(3, Number(groupSize) || 2));
  const list = (visibleTechnicians || [])
    .map((name) => String(name || '').trim())
    .filter(Boolean);
  const next = {};
  for (let i = 0; i + 1 < list.length; i += size) {
    const group = list.slice(i, i + size);
    if (group.length < 2) break;
    const host = group[0];
    const members = group.slice(1).filter((name) => name && name !== host);
    if (!host || !members.length) continue;
    next[host] = members.length === 1 ? members[0] : members;
  }
  return next;
}

export function areTechnicianColumnStacksEqual(left, right) {
  const a = left && typeof left === 'object' ? left : {};
  const b = right && typeof right === 'object' ? right : {};
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) => (
    normalizeStackMembers(a[key]).join('\0') === normalizeStackMembers(b[key]).join('\0')
  ));
}

/** Empilha pares consecutivos (A+B, C+D…) — visualização 1×2. */
export function applyVerticalPairStacks(visibleTechnicians) {
  return applyVerticalGroupStacks(visibleTechnicians, 2);
}

/** Altura atual do empilhamento (1, 2 ou 3). */
export function getTechnicianStackGroupSize(stacks) {
  if (!stacks || typeof stacks !== 'object') return 1;
  let max = 1;
  Object.values(stacks).forEach((value) => {
    const size = 1 + normalizeStackMembers(value).length;
    if (size > max) max = size;
  });
  return Math.min(3, max);
}

/** Remove o stack do host (liberta o bottom). */
export function unstackTechnicianColumn(stacks, hostTech) {
  const host = String(hostTech || '').trim();
  if (!host || !stacks || typeof stacks !== 'object') return {};
  const next = { ...stacks };
  delete next[host];
  return next;
}

/** Se o bottom some do board, limpa entradas inválidas. */
export function pruneTechnicianColumnStacks(stacks, visibleTechnicians) {
  const vis = new Set((visibleTechnicians || []).map((t) => String(t || '').trim()).filter(Boolean));
  if (!stacks || typeof stacks !== 'object') return {};
  const next = {};
  Object.entries(stacks).forEach(([h, b]) => {
    const host = String(h || '').trim();
    const members = normalizeStackMembers(b).filter((name) => vis.has(name) && name !== host);
    if (!host || !vis.has(host) || !members.length) return;
    next[host] = members.length === 1 ? members[0] : members;
  });
  return next;
}
